/**
 * ConsequenceService - Handles consequence creation and management
 */

import { Timestamp, runTransaction, doc, collection } from 'firebase/firestore';
import { db } from '../../../firebase';
import { ConsequenceRepository } from '../repositories/ConsequenceRepository';
import { duplicateDetentionDetector } from '../../../utils/DuplicateDetentionDetector';
import { DISCIPLINE_TYPES } from '../constants';
import {
  getOffenceField,
  validateDisciplineRecord
} from '../utils/fieldNormalizer';
import {
  calculateMentionRuleSeverity,
  calculateAccountabilitySeverity
} from '../utils/severityCalculator';
import { typeNormalizer } from '../utils/TypeNormalizer';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { statsOrchestrator } from '../stats';
import { studentDataFetcher } from '../rules/StudentDataFetcher';
import NotificationService from '../../../features/notifications/services/NotificationService';
import {
  deterministicAutoConsequenceId,
  deterministicManualConsequenceId,
  requireAutoConsequenceOrigin,
} from '../utils/consequenceIdentity';
import { escalationMutation } from '../utils/escalationMutation';
import { snapshotStaffIdentity } from '../utils/staffIdentity';

function requireResolvedType(typeDoc, type, schoolId) {
  if (typeDoc) return typeDoc;
  throw new Error(
    `Configured consequence type "${type}" could not be resolved ` +
    `for school "${schoolId}". Refresh the configuration and try again.`
  );
}

export class ConsequenceService {
  constructor(consequenceRepo = null, ruleEvaluator = null) {
    this.consequenceRepo = consequenceRepo || new ConsequenceRepository();
    this.ruleEvaluator = ruleEvaluator; // Will be set by facade after initialization
  }

  /**
   * Set the rule evaluator (called by facade to avoid circular dependency)
   * @param {Object} ruleEvaluator - RuleEvaluator instance
   */
  setRuleEvaluator(ruleEvaluator) {
    this.ruleEvaluator = ruleEvaluator;
  }

  async resumeAccountabilityConsequence(saved) {
    studentDataFetcher.invalidateAccountabilityCache(saved.studentId);
    studentDataFetcher.invalidateMentionsCache(saved.studentId);
    studentDataFetcher.invalidateConsequencesCache(saved.studentId);
    if (saved.ruleType !== 'accountability-entry' && this.ruleEvaluator) {
      await this.ruleEvaluator.processThresholdRules(saved, saved.studentId);
    }
  }

  /** Resume the original saved check without repeating response side effects. */
  async resumeThresholdFollowup(saved, options = {}) {
    if (saved.thresholdFollowup?.status === 'pending' && !options.skipThresholds && this.ruleEvaluator) {
      studentDataFetcher.invalidateConsequencesCache(saved.studentId);
      await this.ruleEvaluator.processThresholdRules(saved, saved.studentId);
    }
  }

  async recoverSavedConsequence(id, schoolId, options) {
    // Consumption mutations must still pass the writer's saved-mutation checks.
    if (!id || options.accountability || options.threshold) return null;
    const existing = await this.consequenceRepo.getById(id, schoolId);
    if (!existing) return null;
    const persisted = (options.verifyPersistence || existing.thresholdFollowup)
      ? await this.consequenceRepo.getByIdFromServer(id, schoolId)
      : existing;
    if (!persisted) {
      throw new Error('The consequence exists locally but could not be verified on the server.');
    }
    await this.resumeThresholdFollowup(persisted, options);
    // Session confirmation is a UI hint. A missing/retired definition or failed
    // configuration read cannot invalidate an already saved response.
    let savedTypeDoc = null;
    try {
      savedTypeDoc = await typeNormalizer.getTypeDocument(persisted.type, schoolId);
    } catch {
      // The saved session, when present, still supplies its confirmation.
    }
    return {
      ...persisted,
      wasCreated: false,
      persistenceVerified: options.verifyPersistence === true,
      requiresAssignedSession: Boolean(persisted.expectedSession?.start) ||
        savedTypeDoc?.availabilityPolicy?.enabled === true,
    };
  }

  async createDisciplineConsequence(consequenceData, options = {}) {
    try {
      if (!consequenceData.schoolId) throw new Error('A school is required to create a consequence.');
      requireAutoConsequenceOrigin(consequenceData);
      const requestedId = deterministicAutoConsequenceId({
        ...consequenceData, type: options.creationType || consequenceData.type,
      }) || deterministicManualConsequenceId(consequenceData);
      const saved = await this.recoverSavedConsequence(requestedId, consequenceData.schoolId, options);
      if (saved) return saved;

      // Validate and standardize the data
      const validatedData = await validateDisciplineRecord({...consequenceData});

      // Enrich with typeId and typeName (dual-field migration)
      // This enables ID-based queries while maintaining backward compatibility
      let activeTypeDoc = await typeNormalizer.getTypeDocument(
        validatedData.type,
        consequenceData.schoolId
      );
      activeTypeDoc = requireResolvedType(
        activeTypeDoc,
        validatedData.type,
        consequenceData.schoolId,
      );
      validatedData.typeId = activeTypeDoc.id;
      validatedData.typeName = activeTypeDoc.label;

      // Ensure required fields
      if (!validatedData.issuedAt) {
        validatedData.issuedAt = Timestamp.now();
      }
      if (!validatedData.createdAt) {
        validatedData.createdAt = Timestamp.now();
      }
      if (!validatedData.updatedAt) {
        validatedData.updatedAt = Timestamp.now();
      }
      requireAutoConsequenceOrigin(validatedData);
      const creationType = options.creationType || validatedData.type;
      const deterministicId = deterministicAutoConsequenceId({...validatedData, type: creationType})
        || deterministicManualConsequenceId(validatedData);
      // Retain compatibility with older callers that submit a type label/alias.
      // Canonical requests recover above without relying on today's definition.
      if (deterministicId !== requestedId) {
        const normalizedSaved = await this.recoverSavedConsequence(
          deterministicId, validatedData.schoolId, options,
        );
        if (normalizedSaved) return normalizedSaved;
      }

      // Sequential number will be assigned by Cloud Function (consequenceTrigger)
      // DO NOT assign client-side to avoid double-counting
      // The Cloud Function increments atomically and writes back to the document
      const eventType = validatedData.typeId || validatedData.type;
      validatedData.sequentialNumber = null; // Cloud Function will assign
      validatedData.sequentialKey = `${validatedData.studentId}-${eventType}`;

      // Check for duplicate detentions if applicable
      if (validatedData.type === 'detention' || validatedData.type === 'saturday-detention') {
        const duplicateCheck = await duplicateDetentionDetector.checkForDuplicateDetentions(validatedData);
        if (duplicateCheck.hasDuplicates) {
          disciplineLogger.info('Duplicate detention detected - creating resolution instead', { duplicateCount: duplicateCheck.duplicateCount }, 'consequences');
          validatedData.isDuplicate = true;
          validatedData.type = DISCIPLINE_TYPES.DUPLICATE_RESOLUTION;
          validatedData.originalType = consequenceData.type;
        }
      }

      // Check if this consequence should be escalated BEFORE creation (unless skipping)
      // This allows escalation rules to intercept and upgrade consequences
      let escalationResult = options.escalationResult || null;
      if (!options.skipEscalation && this.ruleEvaluator) {
        disciplineLogger.info(`🔍 Checking escalation before creating ${validatedData.type}`, {
          studentId: validatedData.studentId,
          consequenceType: validatedData.type
        }, 'escalations');

        escalationResult = await this.ruleEvaluator.checkEscalationBeforeCreation(
          validatedData.studentId,
          validatedData.type,
          {
            schoolId: validatedData.schoolId, // CRITICAL: Pass schoolId for hierarchical collections
            triggeredBy: validatedData.ruleName || validatedData.triggeredByRule || 'manual',
            referenceDate: consequenceData.issuedAt || consequenceData.createdAt,
            phaseSource: options.phaseSource,
            originalData: consequenceData
          }
        );

        if (escalationResult) {
          disciplineLogger.critical(`⬆️ ESCALATING CONSEQUENCE`, {
            from: validatedData.type,
            to: escalationResult.escalatedType,
            rule: escalationResult.escalationRuleTitle,
            willConsume: escalationResult.consumedDocs.length
          }, 'escalations');

          // Replace the consequence type with the escalated type
          validatedData.type = escalationResult.escalatedType;
          validatedData.escalatedFrom = creationType;
          validatedData.escalatedBy = escalationResult.escalationRuleId;
          validatedData.escalationRuleName = escalationResult.escalationRuleTitle;

          // Update typeId for the escalated type
          const escalatedTypeDoc = await typeNormalizer.getTypeDocument(escalationResult.escalatedType, consequenceData.schoolId);
          activeTypeDoc = requireResolvedType(
            escalatedTypeDoc,
            escalationResult.escalatedType,
            consequenceData.schoolId,
          );
          validatedData.typeId = activeTypeDoc.id;
          validatedData.typeName = activeTypeDoc.label;
        }
      }

      // AUTO-ADJUDICATION: If the consequence type requires adjudication and
      // this consequence was auto-generated by rules, pre-set adjudication status.
      // This ensures the cloud function trigger skips parent notification until
      // an admin reviews and approves the consequence.
      const needsAutoAdjudication = activeTypeDoc.requiresAdjudication === true && validatedData.autoGenerated === true;
      if (needsAutoAdjudication) {
        validatedData.adjudicationStatus = 'pending';
        validatedData.skipRuleEvaluation = true;
        disciplineLogger.info('Auto-adjudication: consequence requires admin review before notification', {
          type: validatedData.type,
          typeId: validatedData.typeId,
          studentId: validatedData.studentId
        }, 'adjudication');
      }

      const escalation = (escalationResult || (!options.skipEscalation && this.ruleEvaluator))
        ? escalationMutation(escalationResult) : undefined;

      // Save the consequence
      let result = deterministicId
        ? await this.consequenceRepo.createWithId(
          validatedData,
          deterministicId,
          { phaseSource: options.phaseSource, creationType, accountability: options.accountability, threshold: options.threshold, escalation },
        )
        : await this.consequenceRepo.create(validatedData, { phaseSource: options.phaseSource, creationType, accountability: options.accountability, threshold: options.threshold, escalation });
      if (options.verifyPersistence) {
        const persisted = await this.consequenceRepo.getByIdFromServer(
          result.id,
          validatedData.schoolId,
        );
        if (!persisted) {
          throw new Error('The consequence write completed but could not be verified on the server.');
        }
        result = {...result, ...persisted, persistenceVerified: true};
      }
      // UI-only hint: the persisted type remains the source of truth, while
      // Quick Issue can wait for the server-assigned session without delaying
      // consequences that do not use Action Availability.
      result.requiresAssignedSession =
        activeTypeDoc?.availabilityPolicy?.enabled === true;
      if (options.accountability) {
        studentDataFetcher.invalidateAccountabilityCache(validatedData.studentId);
        studentDataFetcher.invalidateMentionsCache(validatedData.studentId);
      }
      if (result.wasCreated === false) {
        await this.resumeThresholdFollowup(result, options);
        return result;
      }

      // Create adjudication document for auto-adjudicated consequences
      if (result?.autoGenerated === true && result.adjudicationStatus === 'pending' && !result.adjudicationId && result.id) {
        try {
          const { default: adjudicationService } = await import('./AdjudicationService');
          await adjudicationService.sendToAdjudication(
            'consequence',
            result.id,
            `Auto-adjudication: ${validatedData.typeName || validatedData.type} requires admin review`,
            {
              schoolId: validatedData.schoolId,
              userId: validatedData.createdBy || 'system',
              userName: validatedData.createdByName || 'System (auto-generated)'
            }
          );
          disciplineLogger.info('Auto-adjudication document created', {
            consequenceId: result.id,
            type: validatedData.type
          }, 'adjudication');
        } catch (adjError) {
          // Non-fatal: consequence is created with adjudicationStatus pending,
          // admin can still see it in the queue. The adjudication doc is nice-to-have.
          disciplineLogger.warn('Failed to create auto-adjudication document', {
            consequenceId: result.id,
            error: adjError.message
          }, 'adjudication');
        }
      }

      // Invalidate ONLY consequences cache (targeted invalidation - 75% read reduction)
      // SKIP invalidation for threshold-triggered consequences to preserve consumed flags in memory
      if (!options.skipThresholds) {
        try {
          studentDataFetcher.invalidateConsequencesCache(validatedData.studentId);
          disciplineLogger.debug('✓ Consequences cache invalidated for threshold evaluation', {
            studentId: validatedData.studentId,
            consequenceId: result.id
          });
        } catch (cacheError) {
          // Non-fatal - log but don't fail consequence creation
          disciplineLogger.warn('Failed to invalidate consequences cache', {
            studentId: validatedData.studentId,
            error: cacheError.message
          });
        }
      } else {
        const cacheState = studentDataFetcher.consequencesCache.get(validatedData.studentId);
        disciplineLogger.debug('⏭️ Skipped cache invalidation (threshold-triggered consequence)', {
          studentId: validatedData.studentId,
          consequenceId: result.id,
          consequenceType: validatedData.type,
          cacheExists: !!cacheState,
          cacheAgeMs: cacheState ? Date.now() - cacheState.timestamp : null,
          cacheAgeSec: cacheState ? ((Date.now() - cacheState.timestamp) / 1000).toFixed(1) : null
        });
      }

      disciplineLogger.critical('🎯 CONSEQUENCE CREATED', {
        id: result.id,
        type: validatedData.type,
        studentId: validatedData.studentId,
        studentName: validatedData.studentName,
        triggeredBy: validatedData.ruleName || validatedData.triggeredByRule || 'manual',
        wasEscalated: !!validatedData.escalatedFrom,
        escalatedFrom: validatedData.escalatedFrom,
        willCheckThresholds: !options.skipThresholds,
        skipReason: options.skipThresholds ? 'Avoided recursion' : null
      }, 'consequences');

      const postCreateWarnings = [];

      // If this consequence was escalated, process the escalation cleanup
      // Use cascadeChain to process each rule's reset individually
      if (escalationResult && !result.escalationPersistence && escalationResult.cascadeChain?.length > 0) {
        disciplineLogger.info(`🔄 Processing ${escalationResult.cascadeLevels} escalation cleanup(s)`, {
          cascadeLevels: escalationResult.cascadeLevels
        }, 'escalations');

        // Process reset for EACH escalation in the chain
        for (const escalation of escalationResult.cascadeChain) {
          if (escalation.resetAfterUse && this.ruleEvaluator) {
            disciplineLogger.info(`🔄 Resetting rule: "${escalation.escalationRuleTitle}"`, {
              ruleId: escalation.escalationRuleId,
              consumedCount: escalation.consumedDocs.length
            }, 'escalations');

            try {
              await this.ruleEvaluator.processEscalationReset(
                escalation.escalationRuleId,
                validatedData.studentId,
                escalation.consumedDocs
              );
            } catch (error) {
              if (!options.verifyPersistence) throw error;
              postCreateWarnings.push(`Escalation cleanup: ${error.message}`);
              disciplineLogger.warn('Consequence persisted but escalation cleanup failed', {
                consequenceId: result.id,
                error: error.message,
              }, 'escalations');
            }
          }
        }
      }

      // Check thresholds after EVERY consequence creation (unless explicitly skipped)
      // This ensures escalation rules can trigger after detentions are created
      if (!options.skipThresholds && this.ruleEvaluator) {
        disciplineLogger.info(`🔄 Triggering threshold check for newly created ${validatedData.type}`, null, 'thresholds');
        try {
          await this.ruleEvaluator.processThresholdRules(result, result.studentId);
        } catch (error) {
          if (!options.verifyPersistence) throw error;
          postCreateWarnings.push(`Automatic rule follow-up: ${error.message}`);
          disciplineLogger.warn('Consequence persisted but threshold processing failed', {
            consequenceId: result.id,
            error: error.message,
          }, 'thresholds');
        }
      } else if (options.skipThresholds) {
        disciplineLogger.debug(`⏭️ Skipped threshold check (skipThresholds=true)`, null, 'thresholds');
      }

      // Update aggregated stats (async, don't block)
      statsOrchestrator.onConsequenceCreated(result).catch(err => {
        console.error('Failed to update stats:', err);
      });

      return postCreateWarnings.length > 0
        ? {...result, postCreateWarnings}
        : result;
    } catch (error) {
      console.error('Error creating discipline consequence:', error);
      throw error;
    }
  }

  /**
   * Create a discipline consequence using a transaction-based approach
   * This method creates the consequence first, then checks for escalation
   * and updates it in place if needed, all within a single transaction.
   * This eliminates timing issues with checking escalation before creation.
   * @param {Object} consequenceData - The consequence data to create
   * @param {Object} options - Creation options
   * @returns {Promise<Object>} Created consequence
   */
  async createDisciplineConsequenceTransactional(consequenceData, options = {}) {
    try {
      // Validate and prepare the data first (outside transaction)
      const validatedData = await validateDisciplineRecord(consequenceData);

      // Enrich with typeId and typeName
      const typeDoc = requireResolvedType(
        await typeNormalizer.getTypeDocument(
          validatedData.type,
          consequenceData.schoolId,
        ),
        validatedData.type,
        consequenceData.schoolId,
      );
      validatedData.typeId = typeDoc.id;
      validatedData.typeName = typeDoc.label;

      // Ensure required fields
      if (!validatedData.issuedAt) validatedData.issuedAt = Timestamp.now();
      if (!validatedData.createdAt) validatedData.createdAt = Timestamp.now();
      if (!validatedData.updatedAt) validatedData.updatedAt = Timestamp.now();

      // Store original type if replacing through mention rule
      if (options.replacingType && validatedData.type !== options.replacingType) {
        validatedData.originalType = consequenceData.type;
      }

      const originalType = validatedData.type;
      const studentId = validatedData.studentId;
      const schoolId = validatedData.schoolId;

      // CRITICAL: Validate schoolId for hierarchical collection
      if (!schoolId) {
        throw new Error('schoolId is required for hierarchical disciplineQueue collection');
      }

      // Run everything in a transaction
      const result = await runTransaction(db, async (transaction) => {
        // Step 1: Create the consequence document using hierarchical path
        const consequenceRef = doc(collection(db, 'schools', schoolId, 'disciplineQueue'));
        transaction.set(consequenceRef, validatedData);

        disciplineLogger.info(`📝 Created consequence in transaction`, {
          id: consequenceRef.id,
          type: validatedData.type,
          studentId: studentId
        }, 'consequences');

        // Step 2: Check if this consequence should be escalated
        let escalationResult = null;
        if (!options.skipEscalation && this.ruleEvaluator) {
          disciplineLogger.info(`🔍 Checking escalation after creation`, {
            studentId: studentId,
            consequenceType: originalType,
            consequenceId: consequenceRef.id
          }, 'escalations');

          // Now the consequence EXISTS in the transaction, so counting will be accurate
          escalationResult = await this.ruleEvaluator.checkEscalationAfterCreation(
            transaction,
            studentId,
            originalType,
            consequenceRef.id,
            {
              schoolId: schoolId, // CRITICAL: Pass schoolId for hierarchical queries
              triggeredBy: validatedData.ruleName || validatedData.triggeredByRule || 'manual',
              referenceDate: consequenceData.issuedAt || consequenceData.createdAt,
              phaseSource: options.phaseSource,
              originalData: consequenceData
            }
          );

          if (escalationResult) {
            disciplineLogger.critical(`⬆️ ESCALATING CONSEQUENCE (in transaction)`, {
              id: consequenceRef.id,
              from: originalType,
              to: escalationResult.escalatedType,
              rule: escalationResult.escalationRuleTitle
            }, 'escalations');

            // Step 3: Update the consequence with the escalated type
            const escalatedTypeDoc = await typeNormalizer.getTypeDocument(escalationResult.escalatedType, consequenceData.schoolId);
            requireResolvedType(
              escalatedTypeDoc,
              escalationResult.escalatedType,
              consequenceData.schoolId,
            );

            transaction.update(consequenceRef, {
              type: escalationResult.escalatedType,
              typeId: escalatedTypeDoc?.id || null,
              typeName: escalatedTypeDoc?.label || null,
              escalatedFrom: originalType,
              escalatedBy: escalationResult.escalationRuleId,
              escalationRuleName: escalationResult.escalationRuleTitle,
              updatedAt: Timestamp.now()
            });

            // Step 4: Mark consumed consequences as used (in same transaction)
            if (escalationResult.consumedDocs && escalationResult.consumedDocs.length > 0) {
              for (const consumedDoc of escalationResult.consumedDocs) {
                transaction.update(consumedDoc.ref, {
                  usedByEscalationRule: escalationResult.escalationRuleId,
                  usedByEscalationAt: Timestamp.now()
                });
              }

              disciplineLogger.info(`✓ Marked ${escalationResult.consumedDocs.length} consequences as consumed`, {
                ruleId: escalationResult.escalationRuleId
              }, 'escalations');
            }
          }
        }

        // Return the final data
        return {
          id: consequenceRef.id,
          ...validatedData,
          ...(escalationResult ? {
            type: escalationResult.escalatedType,
            escalatedFrom: originalType,
            escalatedBy: escalationResult.escalationRuleId,
            escalationRuleName: escalationResult.escalationRuleTitle
          } : {})
        };
      });

      disciplineLogger.critical('🎯 TRANSACTION COMPLETE - CONSEQUENCE CREATED', {
        id: result.id,
        type: result.type,
        studentId: result.studentId,
        wasEscalated: !!result.escalatedFrom,
        escalatedFrom: result.escalatedFrom
      }, 'consequences');

      // Process threshold rules AFTER the transaction completes
      if (!options.skipThresholds && this.ruleEvaluator) {
        disciplineLogger.info(`🔍 Checking threshold rules after transaction`, {
          consequenceId: result.id,
          consequenceType: result.type
        }, 'thresholds');

        try {
          await this.ruleEvaluator.processThresholdRules(result);
          disciplineLogger.info(`✓ Threshold processing completed successfully`, {
            consequenceId: result.id
          }, 'thresholds');
        } catch (thresholdError) {
          // CRITICAL: Log threshold processing failure but don't fail the consequence
          console.error('Threshold processing failed after consequence creation:', thresholdError);
          disciplineLogger.error('⚠️ Threshold processing failed', {
            consequenceId: result.id,
            error: thresholdError.message,
            stack: thresholdError.stack
          }, 'thresholds');
          
          // Note: We don't rollback the consequence because:
          // 1. The consequence creation and escalation was successful
          // 2. Threshold rules are separate business logic
          // 3. Manual cleanup can be performed if needed
        }
      }

      // Create notification for consequence assignment
      try {
        await NotificationService.createNotification({
          type: 'consequence',
          priority: 'high',
          category: 'discipline',
          title: 'Consequence Assigned',
          message: `${validatedData.studentName || 'Student'} was assigned ${validatedData.typeName || validatedData.type}`,
          metadata: {
            consequenceId: result.id,
            studentId: validatedData.studentId,
            studentName: validatedData.studentName,
            consequenceName: validatedData.typeName || validatedData.type,
            consequenceTypeId: validatedData.typeId,
            assignedBy: validatedData.issuedById,
            assignedByName: validatedData.issuedByName
          },
          schoolId: validatedData.schoolId,
          recipientIds: [validatedData.issuedById], // Notify the assigner
          roleFilter: ['staff', 'admin'],
          createdById: validatedData.issuedById,
          actionable: true,
          actionUrl: result.id ? `/discipline/queue#${result.id}` : '/discipline/queue',
          actionLabel: 'View Queue',
          expiresInDays: 7
        });
      } catch (notificationError) {
        console.warn('Failed to create consequence notification:', notificationError);
        // Don't fail the consequence creation if notification fails
      }

      return result;
    } catch (error) {
      console.error('Error in transactional consequence creation:', error);
      throw error;
    }
  }

  /**
   * Get the earliest timestamp from triggering mentions
   * This ensures consequences appear inline with their triggers in the timeline
   * @param {Array} mentions - Array of triggering mentions
   * @returns {Timestamp} Earliest mention timestamp
   */
  getEarliestMentionTimestamp(mentions) {
    if (!mentions || mentions.length === 0) {
      return Timestamp.now();
    }

    // Find the earliest mention by timestamp
    const earliest = mentions.reduce((oldest, m) => {
      const mTime = m.issuedAt || m.createdAt;
      const oTime = oldest.issuedAt || oldest.createdAt;

      // Handle Firestore Timestamp objects
      const mSeconds = mTime?.seconds || mTime?.getTime?.() / 1000 || 0;
      const oSeconds = oTime?.seconds || oTime?.getTime?.() / 1000 || 0;

      return (mSeconds < oSeconds) ? m : oldest;
    });

    return earliest.issuedAt || earliest.createdAt || Timestamp.now();
  }

  /**
   * Create consequence from mention rule
   * @param {Object} rule - The rule that triggered
   * @param {Object} mention - The current mention
   * @param {Array} triggeringMentions - All mentions that triggered the rule
   * @returns {Promise<Object>} Created consequence
   */
  async createMentionRuleConsequence(rule, mention, triggeringMentions) {
    try {
      // Determine proper type for the unified collection
      const itemType = rule.consequenceType || 'demerit';
      const requiresAction = itemType === 'detention' || itemType.includes('detention');
      const status = itemType === 'demerit' ? 'archived' : 'pending';

      const queueItem = {
        studentId: mention.studentId,
        studentName: mention.studentName,
        className: mention.className,
        classId: mention.classId,
        subjectId: mention.subjectId || null,
        subject: mention.subject || null,
        subjectProvenance: mention.subjectProvenance || null,
        subjectContextRequired: mention.subjectContextRequired === true,
        schoolId: mention.schoolId,
        type: itemType,
        status: status,
        requiresAction: requiresAction,
        triggeredByRule: rule.id,
        ruleName: rule.title,
        ruleType: 'mention',  // Add rule type for proper labeling
        triggeredBy: triggeringMentions.map(m => m.id),
        triggeringMentions: triggeringMentions.map(m => m.id),
        consequenceType: rule.consequenceType,
        consequenceCount: rule.consequenceCount,
        consequenceDetails: this.generateMentionRuleConsequenceDetails(rule, triggeringMentions),
        severity: calculateMentionRuleSeverity(rule),
        notes: '',
        ...snapshotStaffIdentity(mention),
        createdBy: 'system-mention-rule',
        autoGenerated: true,
        issuedAt: mention.issuedAt || mention.createdAt || Timestamp.now(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        undone: false,
        actioned: false,
        actionedBy: [],
        actionedAt: null
      };

      const persistedQueueItem = Object.fromEntries(
        Object.entries(queueItem).filter(([, value]) => value !== undefined)
      );
      const result = await this.createDisciplineConsequence(persistedQueueItem, {
        phaseSource: { collection: 'mentions', id: mention.id },
      });
      disciplineLogger.critical('Mention rule consequence created', { id: result.id, type: itemType }, 'consequences');

      return result;
    } catch (error) {
      console.error('Error creating mention rule consequence:', error);
      throw error;
    }
  }

  /**
   * Create threshold consequence
   * @param {Object} threshold - Threshold configuration
   * @param {Object} student - Student information
   * @param {Array} demerits - Demerits that triggered threshold
   * @returns {Promise<Object>} Created consequence
   */
  async createThresholdConsequence(threshold, student, demerits) {
    try {
      // Inherit timestamp from earliest triggering demerit for proper timeline ordering
      const inheritedTimestamp = this.getEarliestMentionTimestamp(demerits);

      const consequenceData = {
        studentId: student.studentId,
        studentName: student.studentName,
        className: student.className,
        classId: student.classId,
        schoolId: demerits[0]?.schoolId || null,
        type: threshold.consequenceType,
        status: 'pending',
        requiresAction: true,
        triggeredByThreshold: true,
        thresholdDetails: {
          demerits: threshold.demerits,
          timeframe: threshold.timeframe,
          consequenceType: threshold.consequenceType,
          description: threshold.description
        },
        triggeredBy: demerits.map(d => d.id),
        triggeringDemerits: demerits.map(d => d.id),
        severity: calculateAccountabilitySeverity(threshold),
        offence: `Accumulated ${threshold.demerits} demerits in ${threshold.timeframe}`,
        notes: threshold.description || '',
        ...snapshotStaffIdentity(demerits[demerits.length - 1] || {}),
        createdBy: 'system-threshold',
        autoGenerated: true,
        issuedAt: inheritedTimestamp,  // ✅ FIXED: Inherit from triggering demerits
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        undone: false,
        actioned: false
      };

      const result = await this.createDisciplineConsequence(consequenceData);
      disciplineLogger.critical('Threshold consequence created', { id: result.id, type: consequenceType }, 'thresholds');

      return result;
    } catch (error) {
      console.error('Error creating threshold consequence:', error);
      throw error;
    }
  }

  /**
   * Generate consequence details for mention rules
   * @param {Object} rule - The rule
   * @param {Array} triggeringMentions - Mentions that triggered
   * @returns {string} Consequence details text
   */
  generateMentionRuleConsequenceDetails(rule, triggeringMentions) {
    const count = triggeringMentions.length;
    const studentName = triggeringMentions[0]?.studentName?.split(' ')[0] || 'Student';
    const scopeText = {
      'same-offense': 'for the same offense',
      'same-category': 'in the same category',
      'same-lesson': 'in the same lesson',
      'any': 'of any type'
    }[rule.mentionScope] || 'mentions';

    let details = `${studentName} received ${count} mention${count > 1 ? 's' : ''} ${scopeText}`;

    if (rule.mentionScope !== 'same-lesson') {
      details += ` in ${rule.timeCount} ${rule.timeframe}${rule.timeCount > 1 ? 's' : ''}`;
    }

    return details + '.';
  }

  /**
   * Generate generic consequence details
   * @param {Object} rule - The rule
   * @param {Array} triggeringMentions - Mentions that triggered
   * @returns {string} Consequence details text
   */
  generateConsequenceDetails(rule, triggeringMentions) {
    const count = triggeringMentions.length;
    const studentName = triggeringMentions[0]?.studentName?.split(' ')[0] || 'Student';
    const reasons = [...new Set(triggeringMentions.map(m => getOffenceField(m)))];

    let details = `${studentName} received ${count} mention${count > 1 ? 's' : ''}`;
    if (reasons.length > 0) {
      details += ` for: ${reasons.join(', ')}`;
    }

    if (rule.conditions?.timeframe) {
      details += ` within ${rule.conditions.timeframe}`;
    }

    return details;
  }

  /**
   * Check if consequence already exists
   * @param {string} studentId - Student ID
   * @param {string} ruleId - Rule ID
   * @param {Date} startDate - Period start date
   * @returns {Promise<boolean>} True if exists
   */
  async checkExistingConsequence(studentId, ruleId, startDate) {
    try {
      const consequences = await this.consequenceRepo.getByTriggerRule(ruleId, {
        studentId,
        undone: false
      });

      return consequences.some(c => {
        const createdAt = c.createdAt?.toDate?.() || new Date(c.createdAt);
        return createdAt >= startDate;
      });
    } catch (error) {
      console.error('Error checking existing consequence:', error);
      return false;
    }
  }

  /**
   * Check if accountability consequence already exists
   * @param {string} studentId - Student ID
   * @param {string} thresholdId - Threshold ID
   * @param {Date} startDate - Period start date
   * @returns {Promise<boolean>} True if exists
   */
  async checkExistingAccountabilityConsequence(studentId, thresholdId, startDate) {
    try {
      return await this.consequenceRepo.checkExistingAccountabilityConsequence(
        studentId,
        thresholdId,
        startDate
      );
    } catch (error) {
      console.error('Error checking existing accountability consequence:', error);
      return false;
    }
  }

  /**
   * Get consequences for student
   * @param {string} studentId - Student ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Student consequences
   */
  async getConsequencesByStudent(studentId, options = {}) {
    try {
      return await this.consequenceRepo.getByStudentId(studentId, options);
    } catch (error) {
      console.error('Error fetching student consequences:', error);
      return [];
    }
  }

  /**
   * Get pending consequences
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} Pending consequences
   */
  async getPendingConsequences(filters = {}) {
    try {
      return await this.consequenceRepo.getPendingConsequences(filters);
    } catch (error) {
      console.error('Error fetching pending consequences:', error);
      return [];
    }
  }

  /**
   * Undo a consequence
   * @param {string} consequenceId - Consequence ID
   * @param {Object} undoDetails - Undo details
   * @returns {Promise<Object>} Updated consequence
   */
  async undoConsequence(consequenceId, undoDetails = {}) {
    try {
      return await this.consequenceRepo.undoConsequence(consequenceId, undoDetails);
    } catch (error) {
      console.error('Error undoing consequence:', error);
      throw error;
    }
  }

  /**
   * Get demerits for threshold checking
   * @param {string} studentId - Student ID
   * @param {Date} startDate - Start date
   * @returns {Promise<Array>} Demerits
   */
  async getDemeritsForThreshold(studentId, startDate) {
    try {
      return await this.consequenceRepo.getDemeritsForThreshold(studentId, startDate);
    } catch (error) {
      console.error('Error fetching demerits for threshold:', error);
      return [];
    }
  }

  /**
   * Get total demerit points
   * @param {string} studentId - Student ID
   * @param {Date} startDate - Start date
   * @returns {Promise<number>} Total points
   */
  async getTotalDemeritPoints(studentId, startDate) {
    try {
      return await this.consequenceRepo.getTotalDemeritPoints(studentId, startDate);
    } catch (error) {
      console.error('Error calculating demerit points:', error);
      return 0;
    }
  }

  /**
   * Mark a consequence as attended
   * @param {string} consequenceId - Consequence ID
   * @param {Object} details - Attendance details
   * @returns {Promise<Object>} Updated consequence
   */
  async markAttended(consequenceId, details = {}) {
    try {
      const updateData = {
        status: 'served',
        servedDate: Timestamp.now(),
        attendanceStatus: 'attended',
        attendanceNotes: details.notes || 'Marked as attended',
        attendanceMarkedBy: details.userId || 'system',
        attendanceMarkedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Add to status history if tracking
      if (details.trackHistory) {
        updateData.statusHistory = updateData.statusHistory || [];
        updateData.statusHistory.push({
          status: 'served',
          timestamp: Timestamp.now(),
          changedBy: details.userId || 'system',
          changedByName: details.userName || 'System',
          reason: details.notes || 'Marked as attended'
        });
      }

      const result = await this.consequenceRepo.update(consequenceId, updateData);
      disciplineLogger.info('Consequence marked as attended', {
        id: consequenceId,
        markedBy: details.userId
      }, 'consequences');

      return result;
    } catch (error) {
      console.error('Error marking consequence as attended:', error);
      throw error;
    }
  }

  /**
   * Mark a consequence as not attended
   * @param {string} consequenceId - Consequence ID
   * @param {string} reason - Reason for not attending
   * @param {Object} details - Additional details
   * @returns {Promise<Object>} Updated consequence
   */
  async markNotAttended(consequenceId, reason, details = {}) {
    try {
      const updateData = {
        status: 'cancelled',
        attendanceStatus: 'not-attended',
        attendanceNotes: reason,
        attendanceMarkedBy: details.userId || 'system',
        attendanceMarkedAt: Timestamp.now(),
        cancelledReason: reason,
        cancelledBy: details.userId || 'system',
        cancelledAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Add to status history if tracking
      if (details.trackHistory) {
        updateData.statusHistory = updateData.statusHistory || [];
        updateData.statusHistory.push({
          status: 'cancelled',
          timestamp: Timestamp.now(),
          changedBy: details.userId || 'system',
          changedByName: details.userName || 'System',
          reason: reason
        });
      }

      const result = await this.consequenceRepo.update(consequenceId, updateData);
      disciplineLogger.info('Consequence marked as not attended', {
        id: consequenceId,
        reason: reason,
        markedBy: details.userId
      }, 'consequences');

      return result;
    } catch (error) {
      console.error('Error marking consequence as not attended:', error);
      throw error;
    }
  }

  /**
   * Reschedule a consequence to a new date
   * @param {string} consequenceId - Consequence ID
   * @param {Date} newDate - New scheduled date
   * @param {string} reason - Reason for rescheduling
   * @param {Object} details - Additional details
   * @returns {Promise<Object>} Updated consequence
   */
  async rescheduleConsequence(consequenceId, newDate, reason, details = {}) {
    try {
      // Get current consequence to preserve original date
      const current = await this.consequenceRepo.getById(consequenceId);

      const updateData = {
        scheduledDate: Timestamp.fromDate(newDate),
        rescheduledFrom: current.scheduledDate || null,
        rescheduledReason: reason,
        rescheduledBy: details.userId || 'system',
        rescheduledAt: Timestamp.now(),
        status: 'scheduled', // Ensure status is scheduled
        updatedAt: Timestamp.now()
      };

      // Add to status history if tracking
      if (details.trackHistory) {
        updateData.statusHistory = updateData.statusHistory || [];
        updateData.statusHistory.push({
          status: 'rescheduled',
          timestamp: Timestamp.now(),
          changedBy: details.userId || 'system',
          changedByName: details.userName || 'System',
          reason: reason,
          originalDate: current.scheduledDate,
          newDate: Timestamp.fromDate(newDate)
        });
      }

      const result = await this.consequenceRepo.update(consequenceId, updateData);
      disciplineLogger.info('Consequence rescheduled', {
        id: consequenceId,
        originalDate: current.scheduledDate?.toDate?.(),
        newDate: newDate,
        reason: reason,
        rescheduledBy: details.userId
      }, 'consequences');

      return result;
    } catch (error) {
      console.error('Error rescheduling consequence:', error);
      throw error;
    }
  }

  /**
   * Excuse a consequence (admin action)
   * @param {string} consequenceId - Consequence ID
   * @param {string} reason - Reason for excusing
   * @param {Object} details - Additional details
   * @returns {Promise<Object>} Updated consequence
   */
  async excuseConsequence(consequenceId, reason, details = {}) {
    try {
      const updateData = {
        status: 'excused',
        excusedReason: reason,
        excusedBy: details.userId || 'system',
        excusedByName: details.userName || 'System',
        excusedAt: Timestamp.now(),
        attendanceStatus: 'excused',
        attendanceNotes: reason,
        updatedAt: Timestamp.now()
      };

      // Add to status history if tracking
      if (details.trackHistory) {
        updateData.statusHistory = updateData.statusHistory || [];
        updateData.statusHistory.push({
          status: 'excused',
          timestamp: Timestamp.now(),
          changedBy: details.userId || 'system',
          changedByName: details.userName || 'System',
          reason: reason
        });
      }

      const result = await this.consequenceRepo.update(consequenceId, updateData);
      disciplineLogger.info('Consequence excused', {
        id: consequenceId,
        reason: reason,
        excusedBy: details.userId
      }, 'consequences');

      return result;
    } catch (error) {
      console.error('Error excusing consequence:', error);
      throw error;
    }
  }
}
